import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Button, IconButton, InputAdornment, Switch, TextField, Tooltip } from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { publicScheduleUrl } from './resourceFormat';
import styles from './ResourceFormPopup.module.css';

/**
 * Agendamento público da agenda: liga/desliga, texto exibido abaixo do nome e,
 * quando a agenda já tem link gerado, o link com o atalho de copiar. Com o
 * agendamento online da empresa desligado, avisa que o link ainda não abre.
 * Com previewSlug (agenda nova ou nome trocado), mostra o link previsto pelo nome
 * digitado, marcado como prévia e sem o atalho de copiar.
 */
function PublicScheduleBlock({ enabled, companyEnabled, bio, slug, previewSlug, onToggle, onBioChange, onCopied }) {
    const isPreview = !!previewSlug;
    const shownSlug = previewSlug || slug;
    const url = shownSlug ? publicScheduleUrl(shownSlug) : '';

    const copy = () => {
        navigator.clipboard.writeText(url);
        if (onCopied) onCopied();
    };

    return (
        <div className={styles.toggleCard}>
            <label className={styles.toggleRow}>
                <span className={styles.blockIcon}><PublicOutlinedIcon fontSize="small" /></span>
                <span className={styles.toggleText}>
                    <span className={styles.blockTitle}>Agendamento público</span>
                    <span className={styles.hint}>Habilita esta agenda na página de agendamento online.</span>
                </span>
                <Switch
                    checked={enabled}
                    onChange={(e) => onToggle(e.target.checked)}
                    color="primary"
                    inputProps={{ 'aria-label': 'Habilitar no agendamento público' }}
                />
            </label>

            {enabled && (
                <div className={styles.toggleBody}>
                    {!companyEnabled && (
                        <Alert
                            severity="warning"
                            action={(
                                <Button component={RouterLink} to="/company/preferences" color="inherit" size="small">
                                    Ligar
                                </Button>
                            )}
                        >
                            O agendamento online da empresa está desligado: o link desta agenda ainda não abre.
                            Ligue em Preferências, no bloco Agendamento Público.
                        </Alert>
                    )}
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="resource-public-bio">Bio / especialidade pública</label>
                        <TextField
                            id="resource-public-bio"
                            value={bio}
                            onChange={(e) => onBioChange(e.target.value)}
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            placeholder="Ex.: Fisioterapeuta especialista em RPG"
                            helperText="Aparece abaixo do nome na página de agendamento."
                            className={styles.input}
                        />
                    </div>
                    {shownSlug && (
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="resource-public-url">
                                Link da agenda
                                {isPreview && <span className={styles.previewTag}>Prévia</span>}
                            </label>
                            <TextField
                                id="resource-public-url"
                                value={url}
                                fullWidth
                                size="small"
                                helperText={isPreview
                                    ? 'Montado pelo nome digitado. O link sai ao salvar e ganha um número no fim se o nome já estiver em uso.'
                                    : 'Gerado a partir do nome da agenda.'}
                                className={styles.input}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: isPreview ? undefined : (
                                        <InputAdornment position="end">
                                            <Tooltip title="Copiar link" arrow>
                                                <IconButton size="small" onClick={copy} aria-label="Copiar link">
                                                    <ContentCopyRoundedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default PublicScheduleBlock;
